import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from "../api";
import { useParams, Link } from 'react-router-dom';
import { useClientPagination } from '../hooks/useClientPagination';
import TablePagination from '../components/TablePagination';
import { formatDateTime } from '../utils/formatDate';

const PORT_SERVICES = {
    21: 'FTP',
    22: 'SSH',
    23: 'Telnet',
    25: 'SMTP',
    53: 'DNS',
    80: 'HTTP',
    110: 'POP3',
    143: 'IMAP',
    443: 'HTTPS',
    465: 'SMTPS',
    587: 'SMTP (Submission)',
    993: 'IMAPS',
    995: 'POP3S',
    3306: 'MySQL',
    3389: 'RDP',
    5432: 'PostgreSQL',
    8080: 'HTTP-Proxy',
    8443: 'HTTPS-Alt'
};

const MScanCheck = () => {
    const { domain: paramDomain } = useParams();
    const [domain, setDomain] = useState(paramDomain || '');
    const [scanData, setScanData] = useState(null);
    const [malwareData, setMalwareData] = useState(null);
    const [httpAnalysisData, setHttpAnalysisData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (paramDomain) {
            setDomain(paramDomain);
        }
    }, [paramDomain]);

    const [showAllPorts, setShowAllPorts] = useState(false);

    const performScan = useCallback(async (targetDomain) => {
        if (!targetDomain) return;

        setLoading(true);
        setError(null);
        setScanData(null);
        setMalwareData(null);
        setHttpAnalysisData(null);

        const majorPorts = Object.keys(PORT_SERVICES).join(',');

        try {
            // Run all scans in parallel resiliently
            const scanResults = await Promise.allSettled([
                api.get('/api/port-scan', {
                    params: {
                        host: targetDomain,
                        ports: majorPorts,
                        proto: 'tcp',
                        concurrency: 100,
                        timeout: 3000
                    }
                }),
                api.get('/api/scan', {
                    params: { domain: targetDomain }
                }),
                api.get('/api/http-analysis', {
                    params: { domain: targetDomain }
                })
            ]);

            const [portRes, malwareRes, httpRes] = scanResults;
            const errorMsgs = [];

            if (portRes.status === 'fulfilled') {
                setScanData(portRes.value.data);
            } else {
                console.error('Port scan failed:', portRes.reason);
                const errMsg = portRes.reason.response?.data?.error || portRes.reason.message || "Failed";
                errorMsgs.push(`Port Status: ${errMsg}`);
            }

            if (malwareRes.status === 'fulfilled') {
                setMalwareData(malwareRes.value.data);
            } else {
                console.error('Malware scan failed:', malwareRes.reason);
                const errMsg = malwareRes.reason.response?.data?.error || malwareRes.reason.message || "Failed";
                errorMsgs.push(`Malware Scan: ${errMsg}`);
            }

            if (httpRes.status === 'fulfilled') {
                setHttpAnalysisData(httpRes.value.data);
            } else {
                console.error('HTTP analysis failed:', httpRes.reason);
                const errMsg = httpRes.reason.response?.data?.error || httpRes.reason.message || "Failed";
                errorMsgs.push(`HTTP Analysis: ${errMsg}`);
            }

            if (errorMsgs.length > 0) {
                setError(errorMsgs.join(' | '));
            }
        } catch (err) {
            console.error('Scan failed:', err);
            const errorMessage = err.response?.data?.error || err.message || "Failed to connect to scanning server.";
            setError(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : String(errorMessage));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (domain && !scanData && !loading && !error) {
            performScan(domain);
        }
    }, [domain, performScan, scanData, loading, error]);

    const filteredPortResults = scanData?.results?.filter(p => {
        const isMajor = PORT_SERVICES.hasOwnProperty(p.port);
        const isOpen = p.open;
        if (!showAllPorts) return isOpen; // Online Only: Show all open ports
        return isOpen || isMajor;         // All: Show all Open + Closed Major (Hide Closed Minor)
    }) || [];

    const portsPag = useClientPagination(filteredPortResults, `${domain}-${showAllPorts}`);

    const headerRows = useMemo(() => {
        if (!httpAnalysisData?.http?.headers) return [];
        return Object.entries(httpAnalysisData.http.headers).map(([key, value]) => ({ key, value: String(value) }));
    }, [httpAnalysisData]);

    const headersPag = useClientPagination(headerRows, domain);

    return (
        <div className="container mt-5">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-0">
                                <h4 className="mb-0">
                                    Malware & Port Scan for: <span className="text-primary">{domain || "Loading..."}</span>
                                </h4>
                                <Link to="/scans" className="btn btn-outline-dark btn-sm">
                                    ← Back to My Scans
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row justify-content-center">
                <div className="row">
                    {/* Malware Scan Column (Left) */}
                    <div className="col-md-6 mb-4">
                        <div className="card shadow-sm h-100 border-0">
                            <div className="card-header bg-danger text-white border-0 py-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <i className="las la-bug me-2"></i> Malware Scan
                                </h5>
                            </div>
                            <div className="card-body">
                                <h6 className="card-subtitle mb-3 text-muted">Domain: <strong className="text-dark">{domain}</strong></h6>

                                {loading && (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-danger" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Checking for malware signatures...</p>
                                    </div>
                                )}

                                {!loading && malwareData && (
                                    <div>
                                        <div className="mb-3 d-flex align-items-center">
                                            <span className="text-muted small me-2 text-uppercase fw-bold">Security Grade</span>
                                            <span className={`badge ${malwareData.grade === 'F' ? 'bg-danger' : 'bg-success'} px-3 py-2 fs-6`}>
                                                {malwareData.grade}
                                            </span>
                                        </div>

                                        {malwareData.htmlIssues && malwareData.htmlIssues.length > 0 ? (
                                            <div className="alert alert-danger-subtle border-danger border-start-4">
                                                <h6 className="alert-heading fw-bold"><i className="las la-exclamation-triangle me-2"></i>Security Flags Found</h6>
                                                <ul className="mb-0 ps-3">
                                                    {malwareData.htmlIssues.map((issue, idx) => (
                                                        <li key={idx} className="small">{issue}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <div className="alert alert-success-subtle border-success border-start-4">
                                                <div className="d-flex align-items-center">
                                                    <i className="las la-check-circle fs-4 me-2"></i>
                                                    <div>
                                                        <p className="mb-0 fw-bold">Clean Content</p>
                                                        <p className="mb-0 small opacity-75">No obvious malware signatures detected in HTML content.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {malwareData.notes && malwareData.notes.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="text-muted small text-uppercase mb-3 fw-bold">Intelligence Notes</h6>
                                                <div className="bg-light p-3 rounded-3">
                                                    <ul className="text-muted small mb-0 ps-3">
                                                        {malwareData.notes.map((note, idx) => (
                                                            <li key={idx} className="mb-1">{note}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Port Scan Column (Right) */}
                    <div className="col-md-6 mb-4">
                        <div className="card shadow-sm h-100 border-0">
                            <div className="card-header bg-primary text-white border-0 py-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <i className="las la-server me-2"></i> Major Port Status
                                </h5>
                                <div className="btn-group btn-group-sm">
                                    <button
                                        className={`btn btn-light ${!showAllPorts ? 'active' : ''}`}
                                        onClick={() => setShowAllPorts(false)}
                                    >Online Only</button>
                                    <button
                                        className={`btn btn-light ${showAllPorts ? 'active' : ''}`}
                                        onClick={() => setShowAllPorts(true)}
                                    >All</button>
                                </div>
                            </div>
                            <div className="card-body">
                                <h6 className="card-subtitle mb-3 text-muted">Host: <strong className="text-dark">{domain}</strong></h6>

                                {loading && (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Probing network services...</p>
                                    </div>
                                )}

                                {error && (
                                    <div className="alert alert-danger border-0 shadow-sm" role="alert">
                                        <i className="las la-exclamation-circle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                {!loading && scanData && (
                                    <div>
                                        <div className="mb-3 d-flex gap-2">
                                            <span className="badge bg-light text-primary border border-primary px-3 py-2">
                                                <i className="las la-list me-1"></i> Major Ports Scanned
                                            </span>
                                            <span className="badge bg-light text-secondary border border-secondary px-3 py-2">
                                                <i className="las la-network-wired me-1"></i> IPv4 / TCP
                                            </span>
                                        </div>

                                        {portsPag.items.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle">
                                                    <thead className="table-light sticky-top">
                                                        <tr>
                                                            <th className="small border-0">Port</th>
                                                            <th className="small border-0">State</th>
                                                            <th className="small border-0">Service</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="border-top-0">
                                                        {portsPag.items.map((portInfo) => (
                                                            <tr key={portInfo.port}>
                                                                <td className="fw-bold">{portInfo.port}</td>
                                                                <td>
                                                                    {portInfo.open ? (
                                                                        <span className="badge rounded-pill bg-success-subtle text-success border border-success px-3">
                                                                            <i className="las la-check-circle me-1"></i>Open
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge rounded-pill bg-danger-subtle text-danger border border-danger px-3">
                                                                            <i className="las la-times-circle me-1"></i>Closed
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted small fw-medium">
                                                                        {PORT_SERVICES[portInfo.port] || 'Other Service'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <TablePagination {...portsPag} onPageChange={portsPag.setCurrentPage} />
                                            </div>
                                        ) : (
                                            <div className="text-center py-5 bg-light rounded-3">
                                                <i className="las la-eye-slash fs-1 text-muted opacity-50"></i>
                                                <p className="mb-0 mt-2 text-muted">No open ports found in this range.</p>
                                            </div>
                                        )}

                                        <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                                            <small className="text-muted fst-italic">Generated: {formatDateTime(scanData.scannedAt)}</small>
                                            <Link to="/scans" className="btn btn-sm btn-outline-primary rounded-pill px-3">Management</Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* HTTP Response Analysis Card (Full Width Below) */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-success text-white">
                                <h5 className="mb-0">HTTP Response & DNS Analysis</h5>
                            </div>
                            <div className="card-body">
                                <h6 className="card-subtitle mb-3 text-muted">Domain: <strong>{domain}</strong></h6>

                                {loading && (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-success" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Analyzing HTTP response...</p>
                                    </div>
                                )}

                                {!loading && httpAnalysisData && (
                                    <div>
                                        {/* HTTP Response Info */}
                                        {httpAnalysisData.http && !httpAnalysisData.http.error && (
                                            <div className="mb-4">
                                                <h6 className="border-bottom pb-2">HTTP Response</h6>
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <strong>Status:</strong> <span className={`badge ${httpAnalysisData.http.status < 400 ? 'bg-success' : 'bg-danger'}`}>{httpAnalysisData.http.status} {httpAnalysisData.http.statusText}</span>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <strong>Response Time:</strong> {httpAnalysisData.http.responseTime}ms
                                                        {httpAnalysisData.http.responseTime > 2000 && (
                                                            <span className="badge bg-danger ms-2" title="Slow response time may indicate performance issues">
                                                                <i className="las la-exclamation-triangle me-1"></i>SLOW
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="col-md-3">
                                                        <strong>Server:</strong> {httpAnalysisData.http.server}
                                                        {(httpAnalysisData.http.server.toLowerCase().includes('apache/2.2') ||
                                                            httpAnalysisData.http.server.toLowerCase().includes('apache/2.0') ||
                                                            httpAnalysisData.http.server.toLowerCase().includes('nginx/1.0') ||
                                                            httpAnalysisData.http.server.toLowerCase().includes('iis/6.0') ||
                                                            httpAnalysisData.http.server.toLowerCase().includes('iis/7.0')) && (
                                                                <span className="badge bg-danger ms-2" title="Outdated server version detected">
                                                                    <i className="las la-exclamation-circle me-1"></i>OUTDATED
                                                                </span>
                                                            )}
                                                    </div>
                                                    <div className="col-md-3">
                                                        <strong>Content-Type:</strong> {httpAnalysisData.http.contentType}
                                                        {(httpAnalysisData.http.contentType.includes('application/octet-stream') ||
                                                            httpAnalysisData.http.contentType.includes('application/x-msdownload')) && (
                                                                <span className="badge bg-warning ms-2" title="Unusual content type">
                                                                    <i className="las la-question-circle me-1"></i>UNUSUAL
                                                                </span>
                                                            )}
                                                    </div>
                                                </div>
                                                {httpAnalysisData.http.warning && (
                                                    <div className="alert alert-warning mt-2 mb-0">
                                                        <i className="las la-exclamation-triangle me-2"></i>{httpAnalysisData.http.warning}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {httpAnalysisData.http && httpAnalysisData.http.error && (
                                            <div className="alert alert-danger">
                                                <i className="las la-exclamation-circle me-2"></i>{httpAnalysisData.http.error}
                                            </div>
                                        )}

                                        {/* Security Headers */}
                                        {httpAnalysisData.http && httpAnalysisData.http.securityHeaders && (
                                            <div className="mb-4">
                                                <h6 className="border-bottom pb-2">Security Headers</h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <ul className="list-unstyled">
                                                            <li><i className={`las ${httpAnalysisData.http.securityHeaders.strictTransportSecurity ? 'la-check-circle text-success' : 'la-times-circle text-danger'} me-2`}></i>Strict-Transport-Security</li>
                                                            <li><i className={`las ${httpAnalysisData.http.securityHeaders.contentSecurityPolicy ? 'la-check-circle text-success' : 'la-times-circle text-danger'} me-2`}></i>Content-Security-Policy</li>
                                                            <li><i className={`las ${httpAnalysisData.http.securityHeaders.xFrameOptions ? 'la-check-circle text-success' : 'la-times-circle text-danger'} me-2`}></i>X-Frame-Options</li>
                                                        </ul>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <ul className="list-unstyled">
                                                            <li><i className={`las ${httpAnalysisData.http.securityHeaders.xContentTypeOptions ? 'la-check-circle text-success' : 'la-times-circle text-danger'} me-2`}></i>X-Content-Type-Options</li>
                                                            <li><i className={`las ${httpAnalysisData.http.securityHeaders.referrerPolicy ? 'la-check-circle text-success' : 'la-times-circle text-danger'} me-2`}></i>Referrer-Policy</li>
                                                            <li><i className={`las ${httpAnalysisData.http.securityHeaders.permissionsPolicy ? 'la-check-circle text-success' : 'la-times-circle text-danger'} me-2`}></i>Permissions-Policy</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* DNS Records */}
                                        <div className="mb-4">
                                            <h6 className="border-bottom pb-2">DNS Records</h6>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <strong>IPv4 Addresses:</strong>
                                                    {httpAnalysisData.dns.ipv4.length > 0 ? (
                                                        <ul className="mb-2">
                                                            {httpAnalysisData.dns.ipv4.map((ip, idx) => (
                                                                <li key={idx}>
                                                                    {ip}
                                                                    {(ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.16.') || ip.startsWith('127.')) && (
                                                                        <span className="badge bg-danger ms-2" title="Private IP address detected">
                                                                            <i className="las la-exclamation-circle me-1"></i>PRIVATE IP
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-muted">
                                                            None found
                                                            <span className="badge bg-danger ms-2" title="No IPv4 addresses found - DNS issue">
                                                                <i className="las la-exclamation-circle me-1"></i>MISSING
                                                            </span>
                                                        </p>
                                                    )}

                                                    <strong>IPv6 Addresses:</strong>
                                                    {httpAnalysisData.dns.ipv6.length > 0 ? (
                                                        <ul className="mb-2">
                                                            {httpAnalysisData.dns.ipv6.map((ip, idx) => <li key={idx}>{ip}</li>)}
                                                        </ul>
                                                    ) : <p className="text-muted">None found</p>}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Mail Servers (MX):</strong>
                                                    {httpAnalysisData.dns.mx.length > 0 ? (
                                                        <ul className="mb-2">
                                                            {httpAnalysisData.dns.mx.map((mx, idx) => <li key={idx}>{mx.priority} - {mx.exchange}</li>)}
                                                        </ul>
                                                    ) : <p className="text-muted">None found</p>}

                                                    <strong>Nameservers (NS):</strong>
                                                    {httpAnalysisData.dns.ns.length > 0 ? (
                                                        <ul className="mb-2">
                                                            {httpAnalysisData.dns.ns.map((ns, idx) => <li key={idx}>{ns}</li>)}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-muted">
                                                            None found
                                                            <span className="badge bg-danger ms-2" title="No nameservers found - critical DNS issue">
                                                                <i className="las la-exclamation-circle me-1"></i>CRITICAL
                                                            </span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* All Headers */}
                                        {httpAnalysisData.http && httpAnalysisData.http.headers && (
                                            <div>
                                                <h6 className="border-bottom pb-2">All Response Headers</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Header</th>
                                                                <th>Value</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {headersPag.items.map(({ key, value }) => (
                                                                <tr key={key}>
                                                                    <td><code>{key}</code></td>
                                                                    <td>{value}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <TablePagination {...headersPag} onPageChange={headersPag.setCurrentPage} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MScanCheck;
